import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface City {
  citiesId: number;
  name: string;
}
interface Shop {
  shopId: number;
  name: string;
  address: string;
  city: City;
  manager?: { managerId: number; name: string } | null;
}

export default function ManagerAdd() {
  const navigate = useNavigate();

  const [name, setName] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<Shop[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [selectedShop, setSelectedShop] = React.useState<Shop | null>(null);

  const [shops, setShops] = React.useState<Shop[]>([]);

  React.useEffect(() => {
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/shops-search`, {
          params: { q: searchQuery },
        });
        setSearchResults(data);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleAddShop = () => {
    if (!selectedShop) return;
    if (shops.find((s) => s.shopId === selectedShop.shopId)) return;
    setShops((prev) => [...prev, selectedShop]);
    setSelectedShop(null);
    setSearchQuery('');
  };

  const handleRemoveShop = (shopId: number) => {
    setShops((prev) => prev.filter((s) => s.shopId !== shopId));
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/managers`, {
        name,
        shopIds: shops.map((s) => s.shopId),
      });
      navigate(`/managers`);
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, mx: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
        variant="text"
      >
        Назад
      </Button>

      <Typography variant="h5" sx={{ mb: 3 }}>
        Додавання менеджера
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, color: 'text.secondary' }}
        >
          Ім'я менеджера
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ім'я менеджера"
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, color: 'text.secondary' }}
        >
          Додати магазин
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            fullWidth
            size="small"
            options={searchResults}
            getOptionLabel={(o) => `${o.name} — ${o.city?.name ?? ''}`}
            filterOptions={(x) => x}
            loading={searchLoading}
            value={selectedShop}
            onChange={(_, val) => setSelectedShop(val)}
            inputValue={searchQuery}
            onInputChange={(_, val) => setSearchQuery(val)}
            onOpen={async () => {
              if (searchResults.length > 0) return;
              setSearchLoading(true);
              try {
                const { data } = await axios.get(`${API_URL}/shops-search`, {
                  params: { q: '' },
                });
                setSearchResults(data);
              } catch (e) {
                console.error(e);
              } finally {
                setSearchLoading(false);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.shopId}>
                <Box>
                  <Typography fontSize={14}>{option.name}</Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {option.city?.name} · {option.address}
                    {option.manager && (
                      <span style={{ color: '#f44336' }}>
                        {' '}
                        · зайнятий: {option.manager.name}
                      </span>
                    )}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Пошук магазину за назвою..."
              />
            )}
          />
          <Button
            variant="contained"
            onClick={handleAddShop}
            disabled={!selectedShop}
          >
            Додати
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ mb: 3, mt: 3 }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Магазини ({shops.length})
          </Typography>
        </Box>

        {shops.length === 0 ? (
          <Typography
            sx={{ px: 2, py: 2, fontSize: 13, color: 'text.secondary' }}
          >
            Немає доданих магазинів
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Адреса</TableCell>
                  <TableCell>Місто</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shops.map((shop) => (
                  <TableRow key={shop.shopId} hover>
                    <TableCell>{shop.name}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {shop.address}
                    </TableCell>
                    <TableCell>
                      <Chip label={shop.city?.name ?? '—'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveShop(shop.shopId)}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        disabled={saving || !name.trim()}
      >
        {saving ? 'Збереження...' : 'Зберегти'}
      </Button>
    </Box>
  );
}
